/*******************************
 * This component uses jotai async derived atoms, 
 * so it requires suspense wrapper around it when used
/*******************************/
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { currentChartTypeAtom } from 'views/state';

import { EPType } from './config';
import { getEPSourceType } from './helper';
import {
  vulnBySeverityChartDataAtom,
  epCurrentVulnCountAtom,
  severityChartLabelsAtom,
  obsoleteVulnGroupsChartDataAtom,
  patchableVulnBySeverityChartDataAtom,
  vulnBySeverityChartDataCountAtom,
  patchableVulnBySeverityChartDataCountAtom,
  vulnGroupsChartDataAtom,
  vulnByStatusChartDataAtom,
  statusChartLabelsAtom,
  severityFiltersAtom,
  epTypeAtom,
  appliedEPIdsAtom,
  dashboardDataAtom,
  isObsoleteGroupingVulnListingOpenAtom,
  isGroupingByQidVulnListingOpenAtom,
} from './state';
import BarOrDoughnutChart from '../components/BarOrDoughnutChart';
import GroupingChart from '../components/GroupingChart';
import {
  getDrillDownUrl,
  getVulnerabilityStatusValue,
  getSeverityValue,
  getSeverityFilterValues,
  checkNonZeroArray,
  checkNonZeroObj,
} from '../helper';
import { Severity, VulnerabilityStatus } from '../types';

import '../styles.css';

type Props = {
  setModalComponentData: (ModalDialogData) => void;
};

const CurrentCharts = ({ setModalComponentData }: Props) => {
  const epType = useAtomValue(epTypeAtom);
  const currentChartType = useAtomValue(currentChartTypeAtom);
  const severityFilters = useAtomValue(severityFiltersAtom);
  const setCurrentVulnCount = useSetAtom(epCurrentVulnCountAtom);

  const vulnBySeverityChartData = useAtomValue(vulnBySeverityChartDataAtom);
  const severityChartLabels = useAtomValue(severityChartLabelsAtom);
  const obsoleteVulnGroupsChartData = useAtomValue(
    obsoleteVulnGroupsChartDataAtom,
  );
  const patchableVulnBySeverityChartData = useAtomValue(
    patchableVulnBySeverityChartDataAtom,
  );
  const vulnBySeverityChartDataCount = useAtomValue(
    vulnBySeverityChartDataCountAtom,
  );
  const patchableVulnBySeverityChartDataCount = useAtomValue(
    patchableVulnBySeverityChartDataCountAtom,
  );
  const vulnGroupsChartData = useAtomValue(vulnGroupsChartDataAtom);
  const vulnByStatusChartData = useAtomValue(vulnByStatusChartDataAtom);
  const statusChartLabels = useAtomValue(statusChartLabelsAtom);
  const selectedEPIds = useAtomValue(appliedEPIdsAtom);
  const dashboardData = useAtomValue(dashboardDataAtom);

  // grouping charts vulnerabilities listing dialog open/close state
  const [
    isObsoleteGroupingVulnListingOpen,
    setIsObsoleteGroupingVulnListingOpen,
  ] = useAtom(isObsoleteGroupingVulnListingOpenAtom);
  const [isGroupingByQidVulnListingOpen, setIsGroupingByQidVulnListingOpen] =
    useAtom(isGroupingByQidVulnListingOpenAtom);

  function getChartTooltipLabels(sourceData, epType: EPType) {
    const toolTipLabels: string[] = [];
    const external_values = Object.values<number>(sourceData[EPType.External]);
    const internal_values = Object.values<number>(sourceData[EPType.Internal]);
    const ec2_values = Object.values<number>(sourceData[EPType.EC2]);

    if (
      (epType === EPType.Internal || epType === EPType.All) &&
      checkNonZeroArray(internal_values)
    ) {
      toolTipLabels.push(EPType.Internal);
    }

    if (
      (epType === EPType.External || epType === EPType.All) &&
      checkNonZeroArray(external_values)
    ) {
      toolTipLabels.push(EPType.External);
    }

    if (
      (epType === EPType.EC2 || epType === EPType.All) &&
      checkNonZeroArray(ec2_values)
    ) {
      // toolTipLabels.push(EPType.EC2);
      toolTipLabels.push('AWS');
    }

    return toolTipLabels;
  }

  function getVulnByStatusChartTooltipLabels(data, epType: EPType) {
    const toolTipLabels: string[] = [];

    const internal_values = Object.values<number>(data[EPType.Internal]);
    const external_values = Object.values<number>(data[EPType.External]);
    const ec2_values = Object.values<number>(data[EPType.EC2]);

    if (
      (epType === EPType.Internal || epType === EPType.All) &&
      checkNonZeroObj(internal_values)
    ) {
      toolTipLabels.push(EPType.Internal);
    }

    if (
      (epType === EPType.External || epType === EPType.All) &&
      checkNonZeroObj(external_values)
    ) {
      toolTipLabels.push(EPType.External);
    }

    if (
      (epType === EPType.EC2 || epType === EPType.All) &&
      checkNonZeroObj(ec2_values)
    ) {
      // toolTipLabels.push(EPType.EC2);
      toolTipLabels.push('AWS');
    }

    return toolTipLabels;
  }

  function getSeverityChartsDrillDownUrl(
    label: string,
    sourceType: string,
    patchable = false,
    obsolete = false,
  ) {
    return getDrillDownUrl({
      type: 'open',
      severity: getSeverityValue(Severity[label]).toString(),
      sourceType: getEPSourceType(sourceType as EPType),
      issueType: 'Vulnerability',
      qid: '',
      patchable: patchable,
      obsolete: obsolete,
      severity_in: undefined,
      hosts_in: selectedEPIds,
    });
  }

  function getStatusChartsDrillDownUrl(status: string, sourceType: string) {
    // handle statuses, as the labels are customized
    if (status.toLocaleLowerCase().startsWith('new')) {
      status = VulnerabilityStatus.New;
    } else if (status.toLocaleLowerCase().startsWith('reopened')) {
      status = VulnerabilityStatus.Reopened;
    } else if (status.toLocaleLowerCase().startsWith('active')) {
      status = VulnerabilityStatus.Active;
    } else if (status.toLocaleLowerCase().startsWith('fixed')) {
      status = VulnerabilityStatus.Fixed;
    } else if (status.toLocaleLowerCase().startsWith('ignored')) {
      status = VulnerabilityStatus.Ignored;
    }

    return getDrillDownUrl({
      type: getVulnerabilityStatusValue(status as VulnerabilityStatus),
      severity: '',
      sourceType: getEPSourceType(sourceType as EPType),
      issueType: 'Vulnerability',
      qid: '',
      severity_in: getSeverityFilterValues(severityFilters),
      hosts_in: selectedEPIds,
    });
  }

  function getGroupingDrillDownUrl(qid: string, sourceType: string) {
    return getDrillDownUrl({
      type: 'open',
      severity: '',
      sourceType: sourceType,
      issueType: 'Vulnerability',
      qid: qid,
      severity_in: getSeverityFilterValues(severityFilters),
      hosts_in: selectedEPIds,
    });
  }

  function getObsoleteGroupingDrillDownUrl(qid: string, sourceType: string) {
    return getDrillDownUrl({
      type: 'open',
      severity: '',
      sourceType: sourceType,
      issueType: 'Vulnerability',
      qid: qid,
      severity_in: undefined,
      hosts_in: selectedEPIds,
    });
  }

  function onObsoleteChartExpandClick(ModalDialogData) {
    setIsObsoleteGroupingVulnListingOpen(true);
    setModalComponentData(ModalDialogData);
  }

  function onGroupingByQidChartExpandClick(ModalDialogData) {
    if (ModalDialogData) setIsGroupingByQidVulnListingOpen(true);
    setModalComponentData(ModalDialogData);
  }

  return (
    <>
      {vulnBySeverityChartData && (
        <>
          <div className="col-md-4">
            <BarOrDoughnutChart
              chartType={currentChartType}
              title="By Severity"
              labels={severityChartLabels}
              tooltipLabels={getChartTooltipLabels(
                dashboardData.vulns_by_severity,
                epType,
              )}
              datasets={vulnBySeverityChartData}
              severityFilters={severityFilters}
              total_count={vulnBySeverityChartDataCount}
              getDrillDownUrl={getSeverityChartsDrillDownUrl}
              setCurrentVulnCount={setCurrentVulnCount}
            />
          </div>
        </>
      )}
      {patchableVulnBySeverityChartData && (
        <>
          <div className="col-md-4">
            <BarOrDoughnutChart
              chartType={currentChartType}
              title="Patchable"
              labels={severityChartLabels}
              tooltipLabels={getChartTooltipLabels(
                dashboardData.patchable_vulns_by_severity,
                epType,
              )}
              datasets={patchableVulnBySeverityChartData}
              severityFilters={severityFilters}
              total_count={patchableVulnBySeverityChartDataCount}
              getDrillDownUrl={(severity: string, sourceType: string) =>
                getSeverityChartsDrillDownUrl(severity, sourceType, true, false)
              }
            />
          </div>
        </>
      )}
      {vulnByStatusChartData && (
        <div className="col-md-4">
          <BarOrDoughnutChart
            chartType={currentChartType}
            title="By Status"
            labels={statusChartLabels}
            tooltipLabels={getVulnByStatusChartTooltipLabels(
              dashboardData.vulns_by_status,
              epType,
            )}
            datasets={vulnByStatusChartData}
            severityFilters={Object.values(VulnerabilityStatus)}
            getDrillDownUrl={getStatusChartsDrillDownUrl}
          />
        </div>
      )}
      {obsoleteVulnGroupsChartData && (
        <>
          {
            <div className="col-md-6">
              <GroupingChart
                chartType={currentChartType}
                title="Obsolete Vulnerability Groups"
                data={obsoleteVulnGroupsChartData}
                onExpandClick={(ModalDialogData) => {
                  onObsoleteChartExpandClick(ModalDialogData);
                }}
                severityFilters={severityFilters}
                sourceType={getEPSourceType(epType)}
                getDrillDownUrl={getObsoleteGroupingDrillDownUrl}
                isVulnListingOpen={isObsoleteGroupingVulnListingOpen}
              />
            </div>
          }
        </>
      )}
      {vulnGroupsChartData && (
        <>
          {
            <div className="col-md-6">
              <GroupingChart
                chartType={currentChartType}
                title="Group by QIDs"
                data={vulnGroupsChartData}
                onExpandClick={(ModalDialogData) => {
                  onGroupingByQidChartExpandClick(ModalDialogData);
                }}
                severityFilters={severityFilters}
                sourceType={getEPSourceType(epType)}
                getDrillDownUrl={getGroupingDrillDownUrl}
                isVulnListingOpen={isGroupingByQidVulnListingOpen}
              />
            </div>
          }
        </>
      )}
    </>
  );
};

export default CurrentCharts;
